import { Link, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CAlert, CSpinner } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilArrowLeft } from "@coreui/icons";
import {
  usePortfolioTracker,
  type TrackerResultsDto,
  type TrackerStrategyDto,
  type TrackerStrategyResultDto,
} from "../../api/hooks/usePortfolioTracker";
import { routePaths } from "../../routes/route-paths";
import { ProfitOverTime } from "./components/ProfitOverTime";
import { TrackerTiles } from "./components/TrackerTiles";
import { TrackerTrades } from "./components/TrackerTrades";
import {
  formatDay,
  formatDayRange,
  formatMoney,
  formatPercent,
  formatSignedMoney,
  toneOf,
} from "./portfolio-tracker";
import { useTradeTally } from "./useTradeTally";
import { useTradesThrough } from "./useTradesThrough";

/** One strategy in the customer's tracked portfolio: what it made, its profit over time and every trade. */
export function PortfolioTrackerStrategy() {
  const { t } = useTranslation("portfolioTracker");
  const { tradingStrategyId } = useParams();
  const tracker = usePortfolioTracker();

  if (tracker.isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (tracker.isError || !tracker.data) {
    return <CAlert color="danger">{t("page.loadError")}</CAlert>;
  }

  const results = tracker.data.results;
  const result = results?.strategies.find((candidate) => candidate.tradingStrategyId === tradingStrategyId);
  const strategy = tracker.data.strategies.find((candidate) => candidate.tradingStrategyId === tradingStrategyId);

  // Not in the saved setup, or no longer received: the portfolio is the page that says why.
  if (!results || !result || !strategy) {
    return <Navigate to={routePaths.portfolioTracker} replace />;
  }

  return <StrategyPage results={results} result={result} strategy={strategy} asOf={tracker.data.asOf} />;
}

interface StrategyPageProps {
  results: TrackerResultsDto;
  result: TrackerStrategyResultDto;
  strategy: TrackerStrategyDto;
  asOf: string;
}

function StrategyPage({ results, result, strategy, asOf }: StrategyPageProps) {
  const { t } = useTranslation(["portfolioTracker", "tradingStrategies"]);
  const tally = useTradeTally();
  const through = useTradesThrough()(strategy);
  const gains = results.strategies.reduce((sum, candidate) => sum + Math.max(0, candidate.profit), 0);
  const drop = result.biggestDrop;
  const dropped = drop !== null && drop.amount >= 0.5;

  const tiles = [
    { label: t("tiles.allocation"), value: formatPercent(result.allocationPercent), sub: t("strategy.atEachBuy") },
    {
      label: t("tiles.profit"),
      value: formatSignedMoney(result.profit),
      sub: t("strategy.since", { date: formatDay(result.start) }),
      tone: toneOf(result.profit),
    },
    {
      label: t("tiles.share"),
      value: result.shareOfGainsPercent !== null ? formatPercent(result.shareOfGainsPercent) : "—",
      sub: result.profit <= -0.5 ? t("strategy.downSince") : t("strategy.ofGained", { gains: formatSignedMoney(gains) }),
    },
    {
      label: t("tiles.biggestDrop"),
      value: dropped ? `−${formatMoney(drop.amount)}` : "$0",
      sub: dropped ? t("strategy.dropRange", { range: formatDayRange(drop.from, drop.to) }) : t("strategy.noDrop"),
      tone: dropped ? "text-danger" : "",
    },
    { label: t("tiles.trades"), value: t("tally.closed", { count: result.won + result.lost }), sub: tally(result) },
  ];

  return (
    <div className="portfolio-tracker">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-start gap-2 mb-4">
        <div>
          <h1 className="mb-1">{strategy.name}</h1>
          <p className="text-body-secondary mb-0">
            {[
              strategy.direction > 0 ? t(`tradingStrategies:sides.${strategy.direction}`) : null,
              t("strategy.trades", { through }),
              t("strategy.inPortfolioSince", { date: formatDay(result.start) }),
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
        <div className="col-12 col-sm-auto d-grid">
          <Link to={routePaths.portfolioTracker} className="btn btn-outline-primary">
            <CIcon icon={cilArrowLeft} className="me-2" />
            {t("strategy.back")}
          </Link>
        </div>
      </div>

      <div className="tracker-results">
        <TrackerTiles tiles={tiles} />
        <ProfitOverTime name={strategy.name} result={result} asOf={asOf} />
        <TrackerTrades strategy={strategy} result={result} asOf={asOf} />
      </div>
    </div>
  );
}
